import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadRaidOverview, loadRaidOverviewLiveState } from '../../raid-overview/api/raidOverviewApi.js';
import { loadCurrentUnionRoster } from '../../union-roster/api/unionRosterApi.js';
import { composeUnionMembers } from '../../union-roster/domain/composeUnionMembers.js';
import { dedupeInflightRequest } from '../../../shared/api/inflightRequests.js';

const TETRIS_PRACTICE_RPC = 'get_union_raid_tetris_practice';

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function isMissingTable(error) {
  const raw = String(error?.message || '');
  return /relation .* does not exist|could not find the table|schema cache/i.test(raw);
}

function isMissingTetrisRpc(error) {
  const raw = String(error?.message || '');
  return new RegExp(`${TETRIS_PRACTICE_RPC}|function .* does not exist|schema cache`, 'i').test(raw);
}

function createTetrisError(error, fallback = '테트리스 데이터를 불러오지 못했습니다.') {
  const raw = String(error?.message || '');
  const messages = {
    authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
    active_union_membership_required: '활성 유니온 가입 정보를 확인할 수 없습니다.',
    raid_practice_table_not_found: '모의전 기록 테이블을 찾을 수 없습니다.',
  };
  const code = Object.keys(messages).find((item) => raw.includes(item));
  return new Error(messages[code] || raw || fallback);
}

async function loadPracticeRowsDirect(client, unionId) {
  const read = async (tableName) => client
    .from(tableName)
    .select('*')
    .eq('union_id', unionId)
    .limit(5000);

  const canonical = await read('raid_practice_records');
  if (!canonical.error) return Array.isArray(canonical.data) ? canonical.data : [];
  if (!isMissingTable(canonical.error)) throw canonical.error;

  const legacy = await read('mock_records');
  if (legacy.error) throw legacy.error;
  return Array.isArray(legacy.data) ? legacy.data : [];
}

async function loadPracticeRows(client, unionId) {
  const rpcResult = await client.rpc(TETRIS_PRACTICE_RPC);
  if (!rpcResult.error) {
    const rpcUnionId = String(rpcResult.data?.union_id || '');
    if (rpcUnionId && rpcUnionId !== String(unionId || '')) {
      throw new Error('active_union_membership_required');
    }
    return Array.isArray(rpcResult.data?.practice_records)
      ? rpcResult.data.practice_records
      : [];
  }

  if (!isMissingTetrisRpc(rpcResult.error)) throw rpcResult.error;
  return loadPracticeRowsDirect(client, unionId);
}

async function loadRosterMembersSafely(unionId) {
  try {
    const roster = await loadCurrentUnionRoster();
    if (String(roster?.unionId || '') !== String(unionId || '')) return [];
    return Array.isArray(roster?.members) ? roster.members : [];
  } catch (error) {
    if (String(error?.code || '') === 'roster_migration_required') return [];
    throw error;
  }
}

// The plan board, the tetris page and the chat's plan panel each run their own useRaidTetris, so the
// same season can be requested two or three times at once. loadRaidOverview already shares in-flight
// requests; the season and roster queries on top of it did not, and were simply run again.
const raidTetrisRequests = new Map();

function filterSeasonPracticeRows(rows, { unionId, seasonId, seasonNo }) {
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (String(row.union_id || unionId) !== unionId) return false;
    const rowSeasonId = String(row.season_id || '');
    if (seasonId && rowSeasonId) return rowSeasonId === seasonId;
    const rowSeasonNo = numberValue(row.season_no ?? row.season, Number.NaN);
    return Number.isFinite(rowSeasonNo) && rowSeasonNo === seasonNo;
  });
}

export function loadRaidTetris(seasonNo = null) {
  const requested = seasonNo === null || seasonNo === undefined || seasonNo === '' ? null : Number(seasonNo);
  const requestKey = requested == null ? 'current' : String(requested);
  return dedupeInflightRequest(raidTetrisRequests, requestKey, () => loadRaidTetrisCanonical(requested));
}

async function loadRaidTetrisCanonical(seasonNo = null) {
  const client = requireClient();
  const overview = await loadRaidOverview(seasonNo);
  const unionId = String(overview?.unionId || '');
  const selectedSeason = overview?.selectedSeason || null;
  if (!unionId) throw new Error('활성 유니온 가입 정보를 확인할 수 없습니다.');
  if (!selectedSeason) return { ...overview, practiceRecords: [] };

  const [seasonResult, rosterMembers] = await Promise.all([
    client
      .from('raid_seasons')
      .select('id,season_no')
      .eq('union_id', unionId)
      .eq('season_no', selectedSeason.seasonNo)
      .limit(1),
    loadRosterMembersSafely(unionId),
  ]);
  if (seasonResult.error) throw createTetrisError(seasonResult.error);
  const seasonId = String(seasonResult.data?.[0]?.id || '');

  let rows;
  try {
    rows = await loadPracticeRows(client, unionId);
  } catch (error) {
    throw createTetrisError(error);
  }

  const practiceRecords = filterSeasonPracticeRows(rows, { unionId, seasonId, seasonNo: selectedSeason.seasonNo });

  const members = composeUnionMembers({
    rosterMembers,
    fallbackMembers: selectedSeason.members || [],
    records: [
      ...practiceRecords,
      ...(Array.isArray(selectedSeason.records) ? selectedSeason.records : []),
    ],
    maxMembers: 64,
  });

  return {
    unionId,
    unionName: String(overview.unionName || ''),
    viewerRole: String(overview.viewerRole || ''),
    seasons: overview.seasons || [],
    selectedSeason: {
      ...selectedSeason,
      members,
      practiceRecords,
    },
  };
}

// A practice row changed: the season, its bosses and every canonical attack record are untouched, so
// only the practice rows come back instead of reloading the whole season for one edited slot. The
// member list is still recomposed, because a practice row can be the only thing that puts someone on
// it — composeUnionMembers synthesises members the roster did not return.
export async function loadRaidTetrisPracticeState(current) {
  const unionId = String(current?.unionId || '').trim();
  const selectedSeason = current?.selectedSeason || null;
  if (!unionId || !selectedSeason) return current;

  let rows;
  try {
    rows = await loadPracticeRows(requireClient(), unionId);
  } catch (error) {
    throw createTetrisError(error);
  }

  const practiceRecords = filterSeasonPracticeRows(rows, {
    unionId,
    seasonId: String(selectedSeason.id || ''),
    seasonNo: selectedSeason.seasonNo,
  });

  return {
    ...current,
    selectedSeason: {
      ...selectedSeason,
      practiceRecords,
      members: composeUnionMembers({
        rosterMembers: selectedSeason.members || [],
        fallbackMembers: selectedSeason.members || [],
        records: [...practiceRecords, ...(Array.isArray(selectedSeason.records) ? selectedSeason.records : [])],
        maxMembers: 64,
      }),
    },
  };
}

// Keep practice/roster inputs when only canonical attack records changed.
export async function loadRaidTetrisLiveState(current) {
  const next = await loadRaidOverviewLiveState(current);
  if (next === current || !next?.selectedSeason) return next;
  return { ...next, selectedSeason: { ...next.selectedSeason, members: composeUnionMembers({
    rosterMembers: current.selectedSeason.members || [],
    fallbackMembers: next.selectedSeason.members || [],
    records: [...(next.selectedSeason.practiceRecords || []), ...(next.selectedSeason.records || [])],
    maxMembers: 64,
  }) } };
}
